import { useState, useEffect } from "react";
import { loadIngredientCache, saveIngredientCache } from "./utils";
import ProductSearch from "./ProductSearch";
import ShoppingList from "./ShoppingList";
import RecipeBuilder from "./RecipeBuilder";
import IngredientModal from "./IngredientModal";
import { saveRecipeToAirtable, updateRecipeInAirtable, deleteRecipeFromAirtable } from "./airtableHelpers";

const WEBHOOK_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:8788/search"
    : "/search";

export default function App() {
          // Initialize cache on window if not present
          if (!window._ingredientCache) window._ingredientCache = loadIngredientCache();
        // Load recipes from Airtable on mount
        useEffect(() => {
          async function fetchRecipes() {
            const loaded = await import("./airtableHelpers").then(m => m.loadRecipesFromAirtable());
            setRecipes(loaded.length > 0 ? loaded : []);
          }
          fetchRecipes();
        }, []);
      // Add missing addRecipe function
      const addRecipe = async () => {
        if (!newTitle || !newIngredients) return;
        const ingredientsArray = newIngredients.split(",").map((i) => ({ title: i.trim(), image: '', price: '£0.00' }));
        const newRecipe = {
          title: newTitle,
          ingredients: ingredientsArray,
        };
        let saved;
        try {
          saved = await saveRecipeToAirtable(newRecipe);
          if (!saved.id) {
            alert('Failed to save recipe to database.');
            return;
          }
        } catch (err) {
          alert('Failed to save recipe to database.');
          return;
        }
        setRecipes([
          ...recipes,
          {
            id: saved.id,
            title: newTitle,
            ingredients: ingredientsArray,
          },
        ]);
        setNewTitle("");
        setNewIngredients("");
      };
    // Add missing state hooks
    const [recipes, setRecipes] = useState([]);
    const [activeTab, setActiveTab] = useState("search");
    const [selectedRecipeId, setSelectedRecipeId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const [searchResults, setSearchResults] = useState([]);
    const [shoppingList, setShoppingList] = useState([]);
    const [justAdded, setJustAdded] = useState(null);
    const [newTitle, setNewTitle] = useState("");
    const [newIngredients, setNewIngredients] = useState("");
    // Recipes in shopping list state (lifted up)
    const [recipesInShoppingList, setRecipesInShoppingList] = useState([]);
  // Replace ingredient modal state
  const [replaceModal, setReplaceModal] = useState({ recipeIdx: null, ingredientIdx: null });
  // Track which shopping list items have been 'added' (opened Tesco)
  const [addedToTesco, setAddedToTesco] = useState([]);

    // removed stray lines

  const deleteRecipe = async (id) => {
    setRecipes((prev) => prev.filter((r) => r.id !== id));
    if (selectedRecipeId === id) setSelectedRecipeId(null);
    await deleteRecipeFromAirtable(id);
  };

  const searchTescoProducts = async () => {
    if (!searchTerm) return;
    setSearchLoading(true);
    setSearchError(null);
    setSearchResults([]);

    try {
      const res = await fetch(WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ searchTerm }),
      });
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`HTTP ${res.status}: ${errText}`);
      }
      const data = await res.json();
      setSearchResults(Array.isArray(data) ? data : []);
    } catch (error) {
      setSearchError(error.message);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const addProductToRecipe = async (productName, productImage = '', productPrice = '£0.00', productLink = '', productId = '') => {
    if (!selectedRecipeId) {
      alert("Please select a recipe first!");
      return;
    }
    setRecipes((prev) => {
      const updated = prev.map((r) =>
        r.id === selectedRecipeId && !r.ingredients.some((ing) => ing.title === productName && ing.link === productLink && ing.productId === productId)
          ? { ...r, ingredients: [...r.ingredients, { title: productName, image: productImage, price: productPrice, link: productLink, productId }] }
          : r
      );
      // Find the updated recipe and sync to Airtable
      const updatedRecipe = updated.find((r) => r.id === selectedRecipeId);
      if (updatedRecipe) updateRecipeInAirtable(updatedRecipe);
      return updated;
    });
  };

  return (
    <div className="p-2 sm:p-6 max-w-3xl mx-auto">
      <IngredientModal
        isOpen={replaceModal.recipeIdx !== null}
        onClose={() => setReplaceModal({ recipeIdx: null, ingredientIdx: null })}
        title={replaceModal.ingredientIdx === null ? 'Add Ingredient' : 'Replace Ingredient'}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        searchTescoProducts={searchTescoProducts}
        searchLoading={searchLoading}
        searchError={searchError}
        searchResults={searchResults}
        setSearchResults={setSearchResults}
        onProductSelect={(product) => {
          const now = Date.now();
          const cacheKey = product.productId || product.link || product.title.toLowerCase();
          if (!window._ingredientCache) window._ingredientCache = {};
          window._ingredientCache[cacheKey] = {
            price: product.price,
            image: product.image,
            link: product.link,
            productId: product.productId,
            timestamp: now
          };
          if (typeof saveIngredientCache === 'function') saveIngredientCache(window._ingredientCache);
          setRecipes(prev => prev.map((rec, ridx) => {
            if (ridx !== replaceModal.recipeIdx) return rec;
            let newIngredients;
            if (replaceModal.ingredientIdx === null) {
              // Add
              if (rec.ingredients.some(ing => ing.title === product.title && ing.link === product.link && ing.productId === product.productId)) return rec;
              newIngredients = [...rec.ingredients, {
                title: product.title,
                image: product.image,
                price: product.price,
                link: product.link,
                productId: product.productId,
                timestamp: now
              }];
            } else {
              // Replace
              newIngredients = rec.ingredients.map((ing, idx) => idx === replaceModal.ingredientIdx
                ? {
                    title: product.title,
                    image: product.image,
                    price: product.price,
                    link: product.link,
                    productId: product.productId,
                    timestamp: now
                  }
                : ing
              );
            }
            const updated = { ...rec, ingredients: newIngredients };
            updateRecipeInAirtable(updated);
            return updated;
          }));
          setReplaceModal({ recipeIdx: null, ingredientIdx: null });
          setSearchTerm("");
          setSearchResults([]);
        }}
      />
      <h1 className="text-2xl font-bold mb-4 text-center">Recipe Basket</h1>
      {/* Tab Navigation */}
      <div className="flex mb-6 border-b">
        <button
          className={`flex-1 py-2 px-4 text-lg font-semibold border-b-2 transition-colors duration-200 ${activeTab === "search" ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-blue-600"}`}
          onClick={() => setActiveTab("search")}
        >
          Product Search
        </button>
        <button
          className={`flex-1 py-2 px-4 text-lg font-semibold border-b-2 transition-colors duration-200 ${activeTab === "recipes" ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-blue-600"}`}
          onClick={() => setActiveTab("recipes")}
        >
          Recipe Builder
        </button>
        <button
          className={`flex-1 py-2 px-4 text-lg font-semibold border-b-2 transition-colors duration-200 ${activeTab === "shopping" ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-blue-600"}`}
          onClick={() => setActiveTab("shopping")}
        >
          Shopping List
        </button>
      </div>

      {/* Tab Content */}

      {activeTab === "search" && (
        <div className="p-4 border-2 border-gray-400 rounded-xl bg-white shadow-lg mb-6">
          <ProductSearch
            recipes={recipes}
            selectedRecipeId={selectedRecipeId}
            setSelectedRecipeId={setSelectedRecipeId}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            searchLoading={searchLoading}
            searchError={searchError}
            searchResults={searchResults}
            setSearchResults={setSearchResults}
            searchTescoProducts={searchTescoProducts}
            addProductToRecipe={addProductToRecipe}
            shoppingList={shoppingList}
            setShoppingList={setShoppingList}
            justAdded={justAdded}
            setJustAdded={setJustAdded}
          />
        </div>
      )}

      {activeTab === "shopping" && (
        <div className="p-4 border-2 border-gray-400 rounded-xl bg-white shadow-lg mb-6">
          <ShoppingList
            shoppingList={shoppingList}
            setShoppingList={setShoppingList}
            addedToTesco={addedToTesco}
            setAddedToTesco={setAddedToTesco}
            recipes={recipes}
            recipesInShoppingList={recipesInShoppingList}
            setRecipesInShoppingList={setRecipesInShoppingList}
          />
        </div>
      )}

      {activeTab === "recipes" && (
        <RecipeBuilder
          recipes={recipes}
          setRecipes={setRecipes}
          shoppingList={shoppingList}
          setShoppingList={setShoppingList}
          loadIngredientCache={loadIngredientCache}
          saveIngredientCache={saveIngredientCache}
          updateRecipeInAirtable={updateRecipeInAirtable}
          deleteRecipe={deleteRecipe}
          setReplaceModal={setReplaceModal}
          newTitle={newTitle}
          setNewTitle={setNewTitle}
          newIngredients={newIngredients}
          setNewIngredients={setNewIngredients}
          addRecipe={addRecipe}
          recipesInShoppingList={recipesInShoppingList}
          setRecipesInShoppingList={setRecipesInShoppingList}
        />
      )}
    </div>
    
  );
}

